<template>
  <div class="reg-root">
    <button class="reg-theme-toggle" @click="toggleTheme" :title="darkMode?'Switch to Light':'Switch to Dark'">
      <i :class="darkMode?'mdi mdi-white-balance-sunny':'mdi mdi-moon-waning-crescent'"></i>
    </button>

    <div class="reg-wrapper">
      <div class="reg-card">
        <!-- Logo -->
        <div class="text-center mb-4">
          <img
            :src="$brand.logo"
            alt="Hospital Management System"
            class="reg-logo"
            @click="$router.push('/')"
          />
          <p class="reg-subtitle mt-2">Onboard your hospital onto HMS</p>
        </div>

        <!-- Stepper -->
        <div v-if="step < 3" class="reg-stepper mb-4">
          <div class="reg-step" :class="{ active: step === 1, done: step > 1 }">
            <div class="reg-step-num"><i v-if="step > 1" class="mdi mdi-check"></i><span v-else>1</span></div>
            <div class="reg-step-label">Hospital</div>
          </div>
          <div class="reg-step-line" :class="{ done: step > 1 }"></div>
          <div class="reg-step" :class="{ active: step === 2 }">
            <div class="reg-step-num"><span>2</span></div>
            <div class="reg-step-label">Your Account</div>
          </div>
        </div>

        <!-- ===== STEP 1: HOSPITAL INFO ===== -->
        <div v-if="step === 1">
          <h5 class="text-center fw-bold mb-4 reg-title">Tell Us About Your Hospital</h5>

          <div class="mb-3">
            <label class="auth-label">Hospital Name <span class="text-danger">*</span></label>
            <div class="input-group">
              <span class="input-group-text auth-input-icon"><i class="mdi mdi-hospital-building"></i></span>
              <input v-model="form.hospitalName" type="text" class="form-control auth-input" placeholder="e.g. Nairobi General Hospital" />
            </div>
          </div>

          <div class="row g-3 mb-3">
            <div class="col-6">
              <label class="auth-label">Country</label>
              <select v-model="form.country" class="form-select auth-input">
                <option value="Kenya">Kenya</option>
                <option value="Uganda">Uganda</option>
                <option value="Tanzania">Tanzania</option>
                <option value="Rwanda">Rwanda</option>
                <option value="Ethiopia">Ethiopia</option>
                <option value="Nigeria">Nigeria</option>
                <option value="Ghana">Ghana</option>
                <option value="South Africa">South Africa</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div class="col-6">
              <label class="auth-label">City</label>
              <input v-model="form.city" type="text" class="form-control auth-input" placeholder="e.g. Nairobi" />
            </div>
          </div>

          <div class="mb-3">
            <label class="auth-label">Hospital Email</label>
            <div class="input-group">
              <span class="input-group-text auth-input-icon"><i class="mdi mdi-email-outline"></i></span>
              <input v-model="form.hospitalEmail" type="email" class="form-control auth-input" placeholder="info@yourhospital.com" />
            </div>
          </div>

          <div class="mb-3">
            <label class="auth-label">Hospital Phone</label>
            <div class="input-group">
              <span class="input-group-text auth-input-icon"><i class="mdi mdi-phone-outline"></i></span>
              <input v-model="form.hospitalPhone" type="tel" class="form-control auth-input" placeholder="+254 20 123 4567" />
            </div>
          </div>

          <div v-if="errorMsg" class="alert reg-alert-danger py-2 px-3">
            <i class="mdi mdi-alert-circle me-1"></i> {{ errorMsg }}
          </div>

          <button class="btn btn-primary w-100 mt-2" @click="nextFromHospital" :disabled="!canProceedHospital">
            Continue <i class="mdi mdi-arrow-right ms-1"></i>
          </button>

          <div class="text-center mt-4 reg-secondary-text">
            Already onboarded?
            <a href="javascript:void(0)" @click="$router.push('/login')" class="reg-link fw-semibold">Sign in</a>
          </div>
        </div>

        <!-- ===== STEP 2: ADMIN USER ===== -->
        <div v-if="step === 2">
          <h5 class="text-center fw-bold mb-4 reg-title">Create Your Admin Account</h5>
          <p class="text-center reg-step2-sub mb-4">You'll be the first super admin for <strong>{{ form.hospitalName }}</strong>.</p>

          <div class="row g-3 mb-3">
            <div class="col-6">
              <label class="auth-label">First Name <span class="text-danger">*</span></label>
              <input v-model="form.firstName" type="text" class="form-control auth-input" placeholder="John" />
            </div>
            <div class="col-6">
              <label class="auth-label">Last Name <span class="text-danger">*</span></label>
              <input v-model="form.lastName" type="text" class="form-control auth-input" placeholder="Mwangi" />
            </div>
          </div>

          <div class="mb-3">
            <label class="auth-label">Username <span class="text-danger">*</span></label>
            <div class="input-group">
              <span class="input-group-text auth-input-icon"><i class="mdi mdi-account"></i></span>
              <input v-model="form.username" type="text" class="form-control auth-input" placeholder="e.g. dr.john" autocomplete="username" />
            </div>
            <div class="auth-hint">You'll use this to sign in. Letters, numbers, dots only.</div>
          </div>

          <div class="mb-3">
            <label class="auth-label">Email <span class="text-danger">*</span></label>
            <div class="input-group">
              <span class="input-group-text auth-input-icon"><i class="mdi mdi-email-outline"></i></span>
              <input v-model="form.email" type="email" class="form-control auth-input" placeholder="john@yourhospital.com" />
            </div>
          </div>

          <div class="mb-3">
            <label class="auth-label">Phone</label>
            <div class="input-group">
              <span class="input-group-text auth-input-icon"><i class="mdi mdi-phone-outline"></i></span>
              <input v-model="form.phone" type="tel" class="form-control auth-input" placeholder="+254 712 345 678" />
            </div>
          </div>

          <div class="mb-3">
            <label class="auth-label">Password <span class="text-danger">*</span></label>
            <div class="input-group">
              <span class="input-group-text auth-input-icon"><i class="mdi mdi-lock"></i></span>
              <input :type="showPassword ? 'text' : 'password'" v-model="form.password" class="form-control auth-input" placeholder="Min 8 characters" autocomplete="new-password" />
              <button type="button" class="input-group-text password-toggle" @click="showPassword = !showPassword">
                <i class="mdi" :class="showPassword ? 'mdi-eye-off' : 'mdi-eye'"></i>
              </button>
            </div>
          </div>

          <div class="mb-3">
            <label class="auth-label">Confirm Password <span class="text-danger">*</span></label>
            <div class="input-group">
              <span class="input-group-text auth-input-icon"><i class="mdi mdi-lock-check"></i></span>
              <input :type="showPassword ? 'text' : 'password'" v-model="form.passwordConfirm" class="form-control auth-input" placeholder="Re-enter password" autocomplete="new-password" @keyup.enter="register" />
            </div>
            <div v-if="form.passwordConfirm && form.password !== form.passwordConfirm" class="auth-hint auth-hint-error">
              <i class="mdi mdi-alert-circle me-1"></i>Passwords don't match
            </div>
          </div>

          <div class="mb-3 d-flex align-items-start">
            <input type="checkbox" id="agreeTos" v-model="form.agreeTos" class="reg-checkbox me-2" />
            <label for="agreeTos" class="reg-checkbox-label">
              I agree to the <a href="#" class="reg-link">Terms of Service</a> and <a href="#" class="reg-link">Privacy Policy</a>
            </label>
          </div>

          <div v-if="errorMsg" class="alert reg-alert-danger py-2 px-3">
            <i class="mdi mdi-alert-circle me-1"></i> {{ errorMsg }}
          </div>

          <div class="d-flex gap-2 mt-2">
            <button class="btn btn-outline-secondary" @click="step = 1; errorMsg = ''" :disabled="submitting">
              <i class="mdi mdi-arrow-left me-1"></i> Back
            </button>
            <button class="btn btn-primary flex-grow-1" @click="register" :disabled="!canRegister || submitting">
              <span v-if="submitting"><span class="spinner-border spinner-border-sm me-1"></span> Creating...</span>
              <span v-else><i class="mdi mdi-account-plus me-1"></i> Create Account</span>
            </button>
          </div>
        </div>

        <!-- ===== STEP 3: SUCCESS ===== -->
        <div v-if="step === 3">
          <div class="text-center">
            <div class="reg-success-icon">
              <i class="mdi mdi-check-circle"></i>
            </div>
            <h5 class="fw-bold reg-title">Hospital Onboarded!</h5>
            <p class="reg-success-sub">
              Welcome, {{ form.firstName }}. <strong>{{ form.hospitalName }}</strong> is ready to go.<br>
              Sign in with your username <strong>{{ form.username }}</strong> to start configuring branches, staff and wards.
            </p>
            <button class="btn btn-primary w-100 mt-3" @click="$router.push('/login')">
              <i class="mdi mdi-login me-1"></i> Sign In Now
            </button>
            <button class="btn btn-link w-100 mt-2 reg-secondary-text" @click="$router.push('/')">
              Back to home
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
:root,
html[data-theme="dark"] {
  --hms-h:          #f0f0f0;
  --hms-s:          #a0a0a0;
  --hms-m:          #808080;
  --hms-bg1:        #141414;
  --hms-bg2:        #0a0a0a;
  --hms-bg3:        #1e1e1e;
  --hms-bg4:        #0f0f0f;
  --hms-br:         #2a2a2a;
  --hms-input-br:   #333333;
  --hms-placeholder:#4a4a4a;
  --hms-primary:    #1a7fe8;
  --hms-primary-lt: rgba(26,127,232,0.12);
  --hms-accent:     #F5821F;
  --hms-success:    #28a745;
  --hms-danger:     #dc3545;
  --hms-warning:    #ffc107;
  --hms-info:       #17a2b8;
  --hms-alert-bg:   #2d1111;
  --hms-alert-br:   #5c2020;
  --hms-alert-text: #ff6b6b;
}
html[data-theme="light"] {
  --hms-h:          #1a1a1a;
  --hms-s:          #555555;
  --hms-m:          #777777;
  --hms-bg1:        #ffffff;
  --hms-bg2:        #f4f6f9;
  --hms-bg3:        #eef1f5;
  --hms-bg4:        #ffffff;
  --hms-br:         #dee2e8;
  --hms-input-br:   #ced4da;
  --hms-placeholder:#aaaaaa;
  --hms-primary:    #0047AB;
  --hms-primary-lt: rgba(0,71,171,0.08);
  --hms-accent:     #F5821F;
  --hms-success:    #28a745;
  --hms-danger:     #dc3545;
  --hms-warning:    #ffc107;
  --hms-info:       #17a2b8;
  --hms-alert-bg:   #fff1f1;
  --hms-alert-br:   #f1b5b5;
  --hms-alert-text: #c43d3d;
}
html[data-theme="light"] body,
html[data-theme="dark"] body {
  background: var(--hms-bg2) !important;
  color: var(--hms-h) !important;
}
</style>

<style scoped>
.reg-root {
  background: var(--hms-bg2) !important;
  min-height: 100vh;
  position: relative;
}

.reg-theme-toggle {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 10;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--hms-bg3);
  border: 1px solid var(--hms-br);
  color: var(--hms-primary);
  cursor: pointer;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all .2s;
}
.reg-theme-toggle:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px var(--hms-primary-lt);
}

.reg-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
}

.reg-card {
  background: var(--hms-bg1) !important;
  border: 1px solid var(--hms-br);
  border-radius: 16px;
  padding: 40px 32px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.06);
}

.reg-logo {
  height: 52px;
  cursor: pointer;
}

.reg-subtitle {
  color: var(--hms-s);
  font-size: 13px;
}

.reg-title {
  color: var(--hms-h);
}

.reg-step2-sub {
  color: var(--hms-m);
  font-size: 13px;
}

/* Stepper */
.reg-stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
}
.reg-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.reg-step-num {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--hms-bg3);
  border: 2px solid var(--hms-br);
  color: var(--hms-s);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
  transition: all .2s;
}
.reg-step.active .reg-step-num {
  background: var(--hms-primary);
  border-color: var(--hms-primary);
  color: #fff;
}
.reg-step.done .reg-step-num {
  background: var(--hms-success);
  border-color: var(--hms-success);
  color: #fff;
}
.reg-step-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--hms-s);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.reg-step.active .reg-step-label {
  color: var(--hms-primary);
}
.reg-step-line {
  flex: 0 0 60px;
  height: 2px;
  background: var(--hms-br);
  margin: 0 12px 22px;
  transition: all .2s;
}
.reg-step-line.done {
  background: var(--hms-success);
}

/* Inputs */
.auth-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--hms-m) !important;
  margin-bottom: 6px;
  display: block;
}
.auth-input,
.auth-input-icon,
.password-toggle {
  background: var(--hms-bg4) !important;
  border-color: var(--hms-input-br) !important;
  color: var(--hms-h) !important;
}
.auth-input-icon { color: var(--hms-s) !important; }
.auth-input:focus,
.password-toggle:focus {
  border-color: var(--hms-primary) !important;
  box-shadow: 0 0 0 2px var(--hms-primary-lt) !important;
  outline: none;
}
.auth-input { border-radius: 8px; }
.auth-input::placeholder { color: var(--hms-placeholder) !important; }
.password-toggle { cursor: pointer; }
.auth-hint {
  font-size: 11px;
  color: var(--hms-s);
  margin-top: 4px;
}
.auth-hint-error { color: var(--hms-danger); }

/* Checkbox */
.reg-checkbox { accent-color: var(--hms-primary); margin-top: 3px; }
.reg-checkbox-label {
  font-size: 12px;
  color: var(--hms-m);
}

/* Links */
.reg-link {
  color: var(--hms-primary);
  text-decoration: none;
}
.reg-link:hover { text-decoration: underline; }

.reg-alert-danger {
  background: var(--hms-alert-bg);
  border: 1px solid var(--hms-alert-br);
  color: var(--hms-alert-text);
  font-size: 13px;
}

.reg-secondary-text {
  color: var(--hms-s);
  font-size: 13px;
}

/* Success state */
.reg-success-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(40, 167, 69, 0.12);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}
.reg-success-icon i {
  font-size: 36px;
  color: var(--hms-success);
}
.reg-success-sub {
  color: var(--hms-m);
  font-size: 13px;
  line-height: 1.6;
}

@media (max-width: 480px) {
  .reg-card {
    padding: 28px 20px;
    border-radius: 12px;
  }
  .reg-step-line { flex: 0 0 32px; }
}
</style>

<script>
/* eslint-disable */
import { executeJson, hms } from "@/api";

export default {
  name: "Register",
  data() {
    return {
      darkMode: (localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light") !== "light",
      themeObserver: null,
      step: 1,
      submitting: false,
      showPassword: false,
      errorMsg: "",
      form: {
        // Hospital
        hospitalName: "",
        country: "Kenya",
        city: "",
        hospitalEmail: "",
        hospitalPhone: "",
        // Admin user
        firstName: "",
        lastName: "",
        username: "",
        email: "",
        phone: "",
        password: "",
        passwordConfirm: "",
        agreeTos: false
      }
    };
  },
  computed: {
    canProceedHospital() {
      return !!this.form.hospitalName.trim();
    },
    canRegister() {
      return (
        this.form.firstName.trim() &&
        this.form.lastName.trim() &&
        this.form.username.trim() &&
        this.form.email.trim() &&
        this.form.password.length >= 8 &&
        this.form.password === this.form.passwordConfirm &&
        this.form.agreeTos
      );
    }
  },
  mounted() {
    this.applyTheme();
    this.watchTheme();
    var u = sessionStorage.getItem("userId");
    if (u) this.$router.push("/dashboard");
  },
  beforeUnmount() {
    if (this.themeObserver) this.themeObserver.disconnect();
    window.removeEventListener("storage", this.applyTheme);
  },
  methods: {
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
      this.darkMode = t !== "light";
    },
    toggleTheme() {
      this.darkMode = !this.darkMode;
      var theme = this.darkMode ? "dark" : "light";
      localStorage.setItem("hms_theme", theme);
      document.documentElement.setAttribute("data-theme", theme);
    },
    watchTheme() {
      var self = this;
      this.themeObserver = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type === "attributes" && mutation.attributeName === "data-theme") {
            self.darkMode = document.documentElement.getAttribute("data-theme") !== "light";
          }
        });
      });
      this.themeObserver.observe(document.documentElement, { attributes: true });
      window.addEventListener("storage", this.applyTheme);
    },
    nextFromHospital() {
      this.errorMsg = "";
      if (!this.canProceedHospital) {
        this.errorMsg = "Hospital name is required.";
        return;
      }
      this.step = 2;
    },
    isValidEmail(s) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s || ""); },
    async register() {
      this.errorMsg = "";

      if (!this.isValidEmail(this.form.email)) {
        this.errorMsg = "Please enter a valid email address.";
        return;
      }
      if (this.form.password.length < 8) {
        this.errorMsg = "Password must be at least 8 characters.";
        return;
      }
      if (this.form.password !== this.form.passwordConfirm) {
        this.errorMsg = "Passwords don't match.";
        return;
      }
      if (!this.form.agreeTos) {
        this.errorMsg = "Please agree to the Terms of Service.";
        return;
      }

      this.submitting = true;
      try {
        var r = await executeJson(
          {
            function:        "registerHospital",
            hospital_name:   this.form.hospitalName.trim(),
            country:         this.form.country,
            city:            this.form.city.trim(),
            hospital_email:  this.form.hospitalEmail.trim(),
            hospital_phone:  this.form.hospitalPhone.trim(),
            first_name:      this.form.firstName.trim(),
            last_name:       this.form.lastName.trim(),
            username:        this.form.username.trim(),
            email:           this.form.email.trim(),
            phone:           this.form.phone.trim(),
            password:        this.form.password
          },
          hms
        );
        if (r.data.success) {
          this.step = 3;
        } else {
          this.errorMsg = r.data.message || "Registration failed. Please try again.";
        }
      } catch (e) {
        console.error(e);
        this.errorMsg = "Connection error. Please try again.";
      }
      this.submitting = false;
    }
  }
};
</script>
